import { createCallLog } from '@/factories/callLogFactory'
import { type CallLog } from '@/types/CallLog'
import { formatDistanceToNow } from 'date-fns'

export const CallLogModel = {
  // Factory method
  create: createCallLog,

  // Normalization method
  normalize(data: unknown): CallLog {
    const input = data as Partial<CallLog> & Record<string, unknown>
    const now = new Date()
    return {
      id: input.id as string,
      personId: input.personId as string | undefined,
      organizationId: input.organizationId as string | undefined,
      timestamp:
        input.timestamp instanceof Date
          ? input.timestamp
          : input.timestamp
            ? new Date(input.timestamp as string | number | Date)
            : now,
      direction: (input.direction as CallLog['direction']) || 'outgoing',
      notes: (input.notes as string) || '',
      durationSeconds: input.durationSeconds as number | undefined,
      createdAt:
        input.createdAt instanceof Date
          ? input.createdAt
          : input.createdAt
            ? new Date(input.createdAt as string | number | Date)
            : now,
      updatedAt:
        input.updatedAt instanceof Date
          ? input.updatedAt
          : input.updatedAt
            ? new Date(input.updatedAt as string | number | Date)
            : now,
      tags: (input.tags as string[]) || [],
    }
  },

  // Validation method
  validate(log: CallLog): boolean {
    return (
      !!log.timestamp && !!log.direction && !!log.notes && (!!log.personId || !!log.organizationId)
    )
  },

  // Display methods
  formatTimestamp(callLog: CallLog): string {
    return formatDistanceToNow(callLog.timestamp, { addSuffix: true })
  },

  formatFullTimestamp(callLog: CallLog): string {
    return callLog.timestamp.toLocaleString()
  },

  formatDuration(seconds?: number): string {
    if (!seconds) return 'N/A'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  },

  formatDirection(direction: 'incoming' | 'outgoing'): string {
    return direction.charAt(0).toUpperCase() + direction.slice(1)
  },

  getDirectionIcon(callLog: CallLog): string {
    return callLog.direction === 'incoming' ? 'pi-arrow-down' : 'pi-arrow-up'
  },

  getDirectionClass(callLog: CallLog): string {
    return callLog.direction === 'incoming' ? 'text-blue-500' : 'text-green-500'
  },

  // Helper methods
  isRelatedToPerson(callLog: CallLog, personId: string): boolean {
    return callLog.personId === personId
  },

  isRelatedToOrganization(callLog: CallLog, organizationId: string): boolean {
    return callLog.organizationId === organizationId
  },

  hasTag(callLog: CallLog, tagId: string): boolean {
    return callLog.tags?.includes(tagId) || false
  },
}
